/**
 * Cross-feature Activity API.
 *
 * Lives in `lib/` rather than a feature's `api.ts` because four surfaces need
 * it — the Activity screen, the review desk, the sidebar badge and the
 * provenance chip on Expenses / Documents / listings — and no feature module
 * may import another's `api.ts`.
 */
import {
    homeFeedApiActivityHomesHomeIdGet,
    groupFeedApiActivityGroupsGroupIdGet,
    homeCountApiActivityHomesHomeIdCountGet,
    groupCountApiActivityGroupsGroupIdCountGet,
    markHomeReadApiActivityHomesHomeIdReadPost,
    markGroupReadApiActivityGroupsGroupIdReadPost,
    getHomeByIdApiHomeHomeIdGet,
    getGroupApiExploreGroupsGroupIdGet,
} from '@/client';
import type {
    ActivityCount,
    ActivityEntry,
    ActivityFeedPage,
} from '@/client/types.gen';
import { route } from '@/routes/paths';

/** Which workspace an Activity view belongs to. Manage homes and Explore
 *  groups share every component; only the scope differs. */
export type ActivityScopeType = 'home' | 'group';

export interface ActivityScope {
    type: ActivityScopeType;
    id: string;
}

/**
 * The two lanes. `needs` is waiting on a person and is the only thing the badge
 * counts; `record` is written to and never emptied.
 */
export type ActivityLane = 'needs' | 'record';

/** Who or what put a row here. Drives the provenance chip and nothing else. */
export type ActivityOrigin = NonNullable<ActivityEntry['origin']>;

export type ActivityKind = NonNullable<ActivityEntry['kind']>;

export type { ActivityEntry, ActivityFeedPage, ActivityCount };

export interface ActivityFeedParams {
    lane: ActivityLane;
    origin?: ActivityOrigin;
    kind?: ActivityKind;
    unread?: boolean;
    cursor?: string;
    limit?: number;
}

const unwrap = <T>(result: { data?: T; error?: unknown }): T => {
    if (result.error) throw result.error;
    return result.data as T;
};

export const getActivityFeed = async (
    scope: ActivityScope,
    params: ActivityFeedParams,
): Promise<ActivityFeedPage> => {
    const query = {
        lane: params.lane,
        origin: params.origin,
        kind: params.kind,
        unread: params.unread,
        cursor: params.cursor,
        limit: params.limit,
    };
    return unwrap(
        scope.type === 'home'
            ? await homeFeedApiActivityHomesHomeIdGet({
                  path: { home_id: scope.id },
                  query,
              })
            : await groupFeedApiActivityGroupsGroupIdGet({
                  path: { group_id: scope.id },
                  query,
              }),
    );
};

/**
 * Shared queue totals plus the current viewer's assigned slice. All totals are
 * aggregated by the backend across registered domain action sources.
 */
export const getActivityCount = async (
    scope: ActivityScope,
): Promise<ActivityCount> =>
    unwrap(
        scope.type === 'home'
            ? await homeCountApiActivityHomesHomeIdCountGet({
                  path: { home_id: scope.id },
              })
            : await groupCountApiActivityGroupsGroupIdCountGet({
                  path: { group_id: scope.id },
              }),
    );

/**
 * Mark record rows read. The needs lane has no read state — it has an open
 * loop, which closes by resolving.
 */
export const markActivityRead = async (
    scope: ActivityScope,
    args: { ids?: string[]; all?: boolean },
): Promise<ActivityCount> => {
    const body = { ids: args.ids ?? [], all: args.all ?? false };
    return unwrap(
        scope.type === 'home'
            ? await markHomeReadApiActivityHomesHomeIdReadPost({
                  path: { home_id: scope.id },
                  body,
              })
            : await markGroupReadApiActivityGroupsGroupIdReadPost({
                  path: { group_id: scope.id },
                  body,
              }),
    );
};

/**
 * The workspace's forwarding address.
 *
 * Promoted from a settings field to a header control: it is the one thing a
 * member has to hand out for any of this to work, so it belongs on the page
 * rather than two clicks away.
 */
export const getActivityAddress = async (
    scope: ActivityScope,
): Promise<string | null> => {
    if (scope.type === 'home') {
        const home = unwrap(
            await getHomeByIdApiHomeHomeIdGet({ path: { home_id: scope.id } }),
        );
        return home.inbox_email ?? null;
    }
    const group = unwrap(
        await getGroupApiExploreGroupsGroupIdGet({
            path: { group_id: scope.id },
        }),
    );
    return group.inbox_email ?? null;
};

// ── Routes ───────────────────────────────────────────────────────────────────

export const activityRoute = (scope: ActivityScope): string =>
    scope.type === 'home'
        ? route('homeActivity', { homeId: scope.id })
        : route('groupActivity', { groupId: scope.id });

/** The review desk with nothing selected yet — the queue on its own. */
export const activityReviewRoute = (scope: ActivityScope): string =>
    scope.type === 'home'
        ? route('homeActivityReviewIndex', { homeId: scope.id })
        : route('groupActivityReviewIndex', { groupId: scope.id });

/**
 * The processing log. Home-only: extraction runs against a home's inbox and
 * uploads, so a group scope has no log to show.
 */
export const activityProcessingRoute = (scope: ActivityScope): string | null =>
    scope.type === 'home'
        ? route('homeActivityProcessing', { homeId: scope.id })
        : null;

// ── Query keys ───────────────────────────────────────────────────────────────

/** Shared so a resolve anywhere invalidates every reader. */
export const activityKeys = {
    all: ['activity'] as const,
    count: (scope: ActivityScope) =>
        ['activity', 'count', scope.type, scope.id] as const,
    address: (scope: ActivityScope) =>
        ['activity', 'address', scope.type, scope.id] as const,
    feed: (scope: ActivityScope, params: ActivityFeedParams) =>
        [
            'activity',
            'feed',
            scope.type,
            scope.id,
            params.lane,
            params.origin ?? 'any',
            params.kind ?? 'any',
            params.unread ?? 'any',
            params.cursor ?? 'first',
            params.limit ?? 'default',
        ] as const,
};
